import React, { Component } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Colors } from 'modules/theme';
import H1 from 'components/H1';
import H2 from 'components/H2';
import H3 from 'components/H3';
import H4 from 'components/H4';
import H5 from 'components/H5';
import DatePickerComponent from 'components/Datepicker/DatePickerComponent';
import LineSeparator from 'components/Line';
import Col from 'components/Col';
import Row from 'components/Row';
import Icon from 'components/Icon';
import ListEmpty from 'components/ListEmpty';
import BG from 'components/BG';
import Modal from 'components/Modal';
import Loader from 'components/Loader';
import Input from 'components/InputText';
import ButtonText from 'components/ButtonText';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { Tooltip as TooltipReact } from 'react-tooltip';
import { buildCartridgeConsumptionRequest } from 'utils/cartridgeConsumption';
import {
  waterFilterSetInitialState,
  getWaterFilterCurrentState,
  getWaterFilterCurrentPressure,
  getWaterFilterConsumption,
  getWaterFilterMaxFlowRate,
  getWaterFilterPressureForFlowRate,
  getWaterFilterPressureForConsumption,
  getWaterFilterBypass,
  getTotalConsumptionWaterFilter,
  generateWaterFilterCsv,
  getWaterFilterCurrentBypass,
  getTotalConsumptionCartridgeWf,
  getAllTotalConsumptionCartridgeWf,
  replaceWaterFilterCartridges,
} from 'actions/index';
import domtoimage from 'dom-to-image';
import { PieChart, Pie, Sector, Cell, ResponsiveContainer } from 'recharts';
import PropTypes from 'prop-types';
import { CONSUMPTION_INTERVAL_GROUPS, getIntervalLabel } from 'constants/consumptionIntervals';
import { mergeChartSeries } from 'utils/mergeChartSeries';
import QuickStats, { PressureStatsGrid } from '../../components/QuickStats';
import { ReportWF } from './ReportWF';
import { ModalDatePicker } from './ModalDatePicker';
import ModalCsvFiles from './ModalCsvFiles';
import FlowVolumeChart from './FlowVolumeChart';
import MaxFlowPressureChart from './MaxFlowPressureChart';
import {
  ChartMeta,
  ChartSection,
  ChartViewport,
  ChartViewportFull,
  DateRangeButton,
  IntervalBadge,
  LastUpdatedBadge,
  LastUpdatedDot,
  LastUpdatedLabel,
  LastUpdatedValue,
  StatCard,
  StatLabel,
  StatUnit,
  StatValue,
  StatsRow,
} from './flowVolume.styles';

const Container = styled.div`
  margin: 0px;
  text-align: left;
`;

const GraphContainer = styled.div`
  width: 100%;
  position: relative;
  min-height: ${props => props.height}px;
  display: ${props => props.display ?? 'inherit'};
  justify-content: ${props => props.justifyContent ?? 'center'};
  align-items: ${props => props.alignItems ?? 'center'};
  flex-direction: ${props => props.flexDirection ?? 'row'};
`;

// Evaluated per mount, not at module load: a module-level `new Date()` freezes
// at bundle evaluation, so the chart kept asking for data up to whenever the app
// was opened while the total widgets asked for "now", and the two disagreed by
// everything consumed in between.
const getStartDate = () =>
  moment()
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .add(-7, 'days')
    .toDate();

const getEndDate = () =>
  moment()
    .endOf('day')
    .toDate();

class WF extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cartridgesChangeDate: '',
      showConfiguration: false,
      generatePDF: false,
      downloadPDF: false,
      modalDatePickerConsumption: false,
      datePickerFromConsumption: getStartDate(),
      datePickerToConsumption: getEndDate(),
      consumptionInterval: '1d',
      maxFlowRateInterval: '1d',
      bypassInterval: '1d',
      catridgeSelectedId: null,
      catridgeSelectedObjet: null,
      catridgeCapacity: 0,
      modalViewInstructions: false,
      range: 'days',
      possibleDayRangesConsumption: ['days'],
      possibleDayRangesBypass: ['days'],
      possibleDayRangesMaxFlowRate: ['days'],
      modalDatePickerCsv: false,
      datePickerFromCsv: getStartDate(),
      datePickerToCsv: getEndDate(),
      csvType: 1,
      csvTypeString: 'raw_data',
      modalDeviceId: false,
      new_device_id: '',
      modalBypass: false,
      datePickerFromBypass: getStartDate(),
      datePickerToBypass: getEndDate(),
      modalDatePickerBypass: false,
      modalCartridges: false,
      datePickerToReport: getEndDate(),
      datePickerFromReport: getStartDate(),
      modalDatePickerReport: false,
      datePickerFromMaxFlowRate: getStartDate(),
      datePickerToMaxFlowRate: getEndDate(),
      modalDatePickerMaxFlowRate: false,
      totalCatridgeCapacity: 0,
      modalPressureDetail: false,
    };
  }

  componentDidMount() {
    const {
      waterFilterSetInitialState,
      location: { WaterFilterHead },
    } = this.props;

    const { FlowMeterOfWaterFilters, installed_date, chlorine_or_chloramine } = WaterFilterHead;
    let accumulator = 0;

    // eslint-disable-next-line no-unused-expressions
    FlowMeterOfWaterFilters?.forEach(({ SpotsWaterFilter }) => {
      const { Catridge } = SpotsWaterFilter;
      // Only add to total capacity if not a sediment filter
      if (Catridge.sediment !== true) {
        accumulator += this.calculeCatridgeCapacity(Catridge, chlorine_or_chloramine);
      }
    });

    // Set default date range to last 7 days if installed date allows
    const endDate = moment()
      .endOf('day')
      .toDate();
    const startDate = moment()
      .subtract(7, 'days')
      .startOf('day')
      .toDate();
    const fromDate = moment(installed_date).isAfter(moment(startDate))
      ? moment(installed_date).toDate()
      : startDate;

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      totalCatridgeCapacity: accumulator,
      datePickerFromBypass: fromDate,
      datePickerToBypass: endDate,
    });

    waterFilterSetInitialState();
    if (moment(startDate) < moment(installed_date)) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState(
        {
          datePickerFromConsumption: moment(installed_date).toDate(),
          datePickerFromMaxFlowRate: moment(installed_date).toDate(),
          datePickerFromCsv: moment(installed_date).toDate(),
          datePickerFromBypass: moment(installed_date).toDate(),
        },
        () => this.getInitialData(),
      );
    } else {
      this.getInitialData();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.wf_for_pfd && this.props.wf_for_pfd) {
      this.putDataInPdf();
    }

    const {
      waterFilterConsumption,
      waterFilterConsumptionLoading,
      updateLastUpdated,
      setDeviceDisconnected,
    } = this.props;

    // Check if consumption data loading has completed
    if (
      prevProps.waterFilterConsumptionLoading &&
      !waterFilterConsumptionLoading &&
      waterFilterConsumption
    ) {
      // Check if device is disconnected (last_updated is null)
      if (waterFilterConsumption.last_updated === null && setDeviceDisconnected) {
        setDeviceDisconnected(true);
      }
      // Otherwise update the last updated timestamp if it exists
      else if (waterFilterConsumption.last_updated && updateLastUpdated) {
        updateLastUpdated(waterFilterConsumption.last_updated);
        // Reset disconnected state if it was previously set
        if (setDeviceDisconnected) {
          setDeviceDisconnected(false);
        }
      }
    }
  }

  putDataInPdf = async () => {
    try {
      const {
        location: { id: location_id },
        fetchAllAlerts,
      } = this.props;
      await fetchAllAlerts({ location_id });

      let totalGaloonsChart = document.getElementById('totalGaloonsChart');
      let exhaustionChart = document.getElementById('exhaustionChart');
      let maxFlowAndPressureChart = document.getElementById('maxFlowAndPressureChart');

      totalGaloonsChart = totalGaloonsChart && (await domtoimage.toPng(totalGaloonsChart));
      exhaustionChart = exhaustionChart && (await domtoimage.toPng(exhaustionChart));
      maxFlowAndPressureChart =
        maxFlowAndPressureChart && (await domtoimage.toPng(maxFlowAndPressureChart));

      this.setState({
        totalGaloonsChart,
        exhaustionChart,
        maxFlowAndPressureChart,
        downloadPDF: true,
      });
    } catch (error) {
      this.setState({ generatePDF: false });
      console.log(error);
    }
  };

  getInitialData = () => {
    const {
      location: { WaterFilterHead },
      getWaterFilterCurrentState,
      getWaterFilterCurrentPressure,
      getTotalConsumptionWaterFilter,
      getWaterFilterConsumption,
      getWaterFilterMaxFlowRate,
      getWaterFilterPressureForFlowRate,
      getWaterFilterPressureForConsumption,
      getWaterFilterCurrentBypass,
      setLastUpdatedLoading,
    } = this.props;

    const {
      FlowMeterOfWaterFilters,
      device_id,
      chlorine_or_chloramine,
      id: water_filter_head_id,
      installed_date,
    } = WaterFilterHead;

    if (device_id) {
      const {
        datePickerToConsumption,
        datePickerFromConsumption,
        range,
        consumptionInterval,
        maxFlowRateInterval,
        datePickerFromMaxFlowRate,
        datePickerToMaxFlowRate,
      } = this.state;

      // Set loading state in parent component
      if (setLastUpdatedLoading) {
        setLastUpdatedLoading();
      }

      const catridgeSelectedObjet = FlowMeterOfWaterFilters[0];

      getWaterFilterCurrentBypass({ device_id, from: installed_date });
      getWaterFilterCurrentState({ device_id, from: installed_date });
      getWaterFilterCurrentPressure({ device_id });
      getTotalConsumptionWaterFilter({ device_id, from: installed_date });
      getWaterFilterConsumption({
        device_id,
        from: datePickerFromConsumption,
        to: datePickerToConsumption,
        interval: consumptionInterval,
        range,
      });
      getWaterFilterMaxFlowRate({
        device_id,
        from: datePickerFromMaxFlowRate,
        to: datePickerToMaxFlowRate,
        interval: maxFlowRateInterval,
        range,
      });

      getWaterFilterPressureForConsumption({
        device_id,
        from: datePickerFromConsumption,
        to: datePickerToConsumption,
        interval: consumptionInterval,
        range,
      });

      getWaterFilterPressureForFlowRate({
        device_id,
        from: datePickerFromMaxFlowRate,
        to: datePickerToMaxFlowRate,
        interval: maxFlowRateInterval,
        range,
      });

      if (catridgeSelectedObjet) {
        const catridgeCapacity = this.calculeCatridgeCapacity(
          catridgeSelectedObjet.SpotsWaterFilter.Catridge,
          chlorine_or_chloramine,
        );

        this.setState({
          catridgeSelectedId: catridgeSelectedObjet.SpotsWaterFilter.Catridge.id,
          catridgeSelectedObjet,
          catridgeCapacity,
        });

        if (catridgeSelectedObjet.last_replaced) {
          this.loadCartridgeConsumption(
            catridgeSelectedObjet,
            device_id,
            water_filter_head_id,
            FlowMeterOfWaterFilters,
          );
        }
      }
    }
  };

  loadCartridgeConsumption = (flowMeter, device_id, water_filter_head_id, flowMeters) => {
    const { getTotalConsumptionCartridgeWf, getAllTotalConsumptionCartridgeWf } = this.props;
    const request = buildCartridgeConsumptionRequest({
      device_id,
      flowMeter,
      water_filter_head_id,
    });

    if (request.from && request.spotId) {
      getTotalConsumptionCartridgeWf(request);
    }

    getAllTotalConsumptionCartridgeWf({
      device_id,
      water_filter_head_id,
      flowMeters,
    });
  };

  calculeCatridgeCapacity = (catridge, chlorine_or_chloramine) => {
    // If this is a sediment filter, return 0 capacity
    if (catridge.sediment === true) {
      return 0;
    }

    const capacitySelected =
      chlorine_or_chloramine === 'Chlorine'
        ? catridge.chlorine_capacity
        : catridge.chloramine_capacity;

    return catridge.scale_control_capacity
      ? capacitySelected && catridge.scale_control_capacity > capacitySelected
        ? capacitySelected
        : catridge.scale_control_capacity
      : capacitySelected;
  };

  cartidgeOnChange = evt => {
    const {
      location: {
        WaterFilterHead: {
          id: water_filter_head_id,
          FlowMeterOfWaterFilters,
          device_id,
          chlorine_or_chloramine,
        },
      },
    } = this.props;

    const catridgeSelectedObjet = FlowMeterOfWaterFilters.find(wf => wf.id === evt.target.value);

    if (catridgeSelectedObjet) {
      const {
        last_replaced,
        SpotsWaterFilter: { Catridge },
      } = catridgeSelectedObjet;
      if (last_replaced) {
        this.loadCartridgeConsumption(
          catridgeSelectedObjet,
          device_id,
          water_filter_head_id,
          FlowMeterOfWaterFilters,
        );
      }
      const catridgeCapacity = this.calculeCatridgeCapacity(Catridge, chlorine_or_chloramine);

      this.setState({
        catridgeSelectedId: evt.target.value,
        catridgeSelectedObjet,
        catridgeCapacity,
      });
    }
  };

  renderActiveShape = props => {
    const {
      cx,
      cy,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      allSedimentFilters,
    } = props;
    return (
      <g>
        {!allSedimentFilters && (
          <>
            <text
              x={cx}
              y={cy - 8}
              dy={8}
              textAnchor="middle"
              fontFamily="Gotham, Sans Serif"
              fontSize={25}
              fill={Colors.black}
            >
              {payload.value}%
            </text>
            <text
              x={cx}
              y={cy + 15}
              textAnchor="middle"
              fontFamily="Gotham, Sans Serif"
              fontSize={14}
              fill={Colors.gray8}
            >
              remaining
            </text>
          </>
        )}
        {allSedimentFilters && (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            fontFamily="Gotham, Sans Serif"
            fontSize={14}
            fill={Colors.gray8}
          >
            N/A Capacity
          </text>
        )}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  renderPressure = () => {
    const {
      waterFilterCurrentPressure,
      waterFilterCurrentPressureLoading,
      location: {
        WaterFilterHead: { device_id },
      },
    } = this.props;

    const iconTime = <Icon name="weight" color={Colors.yellow} fontSize={22} />;
    return (
      <GraphContainer
        display="flex"
        justifyContent="space-around"
        flexDirection="column"
        height={360}
      >
        {/* INLET pressure */}
        <Col>
          <H2 textAlign="center" style={{ fontWeight: 'bold' }}>
            Inlet Pressure
          </H2>
          {waterFilterCurrentPressureLoading ? (
            <Loader size={40} block />
          ) : !device_id ? (
            <Col justifyContent="center">
              <ListEmpty message="Installation Not Complete" />
            </Col>
          ) : (
            <>
              {(waterFilterCurrentPressure || []).map((pressure, index) => {
                const inletPressure =
                  pressure &&
                  pressure.inletPressure !== undefined &&
                  pressure.inletPressure !== null &&
                  !isNaN(parseFloat(pressure.inletPressure))
                    ? parseFloat(pressure.inletPressure)
                    : null;

                return (
                  <Row key={index}>
                    <Col lg={100} md={100} sm={100} xs={100}>
                      <H4>
                        Filter {index + 1}
                        <span style={{ marginLeft: 20, marginRight: 20 }}> {iconTime} </span>
                        <span style={{ color: Colors.blue }}>
                          {inletPressure !== null ? Math.round(inletPressure) : 'N/A'}{' '}
                        </span>
                        PSI
                      </H4>
                    </Col>
                  </Row>
                );
              })}
            </>
          )}
        </Col>
        <LineSeparator style={{ marginTop: 5, marginBottom: 5 }} />
        {/* OUTLET Pressure */}
        <Col>
          <H2 textAlign="center" style={{ fontWeight: 'bold' }}>
            Outlet Pressure
          </H2>
          {waterFilterCurrentPressureLoading ? (
            <Loader size={40} block />
          ) : !device_id ? (
            <Col justifyContent="center">
              <ListEmpty message="Installation Not Complete" />
            </Col>
          ) : (
            <>
              {(waterFilterCurrentPressure || []).map((pressure, index) => {
                const outletPressure =
                  pressure &&
                  pressure.outletPressure !== undefined &&
                  pressure.outletPressure !== null &&
                  !isNaN(parseFloat(pressure.outletPressure))
                    ? parseFloat(pressure.outletPressure)
                    : null;

                return (
                  <Row key={index}>
                    <Col lg={100} md={100} sm={100} xs={100}>
                      <H4>
                        Filter {index + 1}
                        <span style={{ marginLeft: 20, marginRight: 20 }}> {iconTime} </span>
                        <span style={{ color: Colors.blue }}>
                          {outletPressure !== null ? Math.round(outletPressure) : 'N/A'}{' '}
                        </span>
                        PSI
                      </H4>
                    </Col>
                  </Row>
                );
              })}
            </>
          )}
        </Col>
        <LineSeparator style={{ marginTop: 5, marginBottom: 5 }} />
        {/* DIFFERENTIAL Pressure */}
        <Col lg={100} md={100} sm={100} xs={100}>
          <H2 textAlign="center" style={{ fontWeight: 'bold' }}>
            Differential Pressure
          </H2>
          {waterFilterCurrentPressureLoading ? (
            <Loader size={40} block />
          ) : !device_id ? (
            <Col justifyContent="center">
              <ListEmpty message="Installation Not Complete" />
            </Col>
          ) : (
            <>
              {(waterFilterCurrentPressure || []).map((pressure, index) => {
                const inletPressure =
                  pressure &&
                  pressure.inletPressure !== undefined &&
                  pressure.inletPressure !== null &&
                  !isNaN(parseFloat(pressure.inletPressure))
                    ? parseFloat(pressure.inletPressure)
                    : null;

                const outletPressure =
                  pressure &&
                  pressure.outletPressure !== undefined &&
                  pressure.outletPressure !== null &&
                  !isNaN(parseFloat(pressure.outletPressure))
                    ? parseFloat(pressure.outletPressure)
                    : null;

                const differential =
                  inletPressure !== null && outletPressure !== null
                    ? Math.max(0, inletPressure - outletPressure)
                    : null;

                const textColor =
                  differential !== null && differential > 25 ? Colors.red : Colors.blue;

                return (
                  <Row key={index}>
                    <Col lg={100} md={100} sm={100} xs={100}>
                      <H4>
                        Filter {index + 1}
                        <span style={{ marginLeft: 20, marginRight: 20 }}> {iconTime} </span>
                        <span style={{ color: textColor }}>
                          {differential !== null ? Math.round(differential) : 'N/A'}{' '}
                        </span>
                        PSI
                      </H4>
                    </Col>
                  </Row>
                );
              })}
            </>
          )}
        </Col>
      </GraphContainer>
    );
  };

  renderBypassFrequency = () => {
    const {
      waterFilterCurrentBypassLoading,
      waterFilterCurrentBypass,
      waterFilterCurrentState,
      location: {
        WaterFilterHead: { device_id },
      },
    } = this.props;

    const flow3Value = waterFilterCurrentState?.flow_3 || 0;
    const bypassActive = Number(waterFilterCurrentBypass) > 0 || Number(flow3Value) > 0;

    return (
      <Col lg={50} md={50} sm={100} xs={100}>
        <H2 style={{ fontWeight: 'bold' }}>Bypass Status</H2>
        {waterFilterCurrentBypassLoading ? (
          <Loader size={60} block />
        ) : !device_id ? (
          <Col justifyContent="center">
            <ListEmpty message="Installation Not Complete" />
          </Col>
        ) : (
          <>
            <Row justifyContent="center">
              {!bypassActive ? <H1 color={Colors.green}>Off</H1> : <H1 color={Colors.red}>On</H1>}
            </Row>
            <H3
              lineHeight="0"
              color={Colors.lightBlue}
              textAlign="center"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                this.switchModal('modalBypass');
                this.calculeWaterFilterBypass();
              }}
            >
              Open More
            </H3>
          </>
        )}
      </Col>
    );
  };

  renderFilterCartridges = flowMeters => {
    const {
      cartridgeAllTotalConsumptionWf,
      cartridgeTotalConsumptionWfLoading,
      cartridgeAllTotalConsumptionWfLoading,
    } = this.props;
    const { totalCatridgeCapacity } = this.state;

    // Assuming all cartridges are replaced together and have the same status
    if (flowMeters && flowMeters.length > 0 && flowMeters[0].last_replaced) {
      const firstCartridge = flowMeters[0];

      // Calculate weeks since filter was replaced
      const lastReplacedMoment = moment(firstCartridge.last_replaced);
      const replacedWeeks = moment().diff(lastReplacedMoment, 'weeks');

      // Check if all filters are sediment filters
      const allSedimentFilters = flowMeters.every(
        meter => meter.SpotsWaterFilter.Catridge.sediment === true,
      );

      // Check for data error: totalCatridgeCapacity is 0 but flow data exists and we don't have all sediment filters
      const hasCapacityDataError =
        !allSedimentFilters && totalCatridgeCapacity === 0 && cartridgeAllTotalConsumptionWf > 0;

      // Calculate flow capacity remaining
      const usedCapacityPercent = hasCapacityDataError
        ? 100 // Assume 100% used if we have a data error
        : totalCatridgeCapacity === 0
        ? 0
        : Math.round((cartridgeAllTotalConsumptionWf * 100) / totalCatridgeCapacity);

      // Remaining capacity (for the pie chart)
      const remainingCapacityPercent = 100 - usedCapacityPercent;

      // Check if capacity is exceeded by flow (percentage-based only)
      // Note: Time-based alerts may be added in the future
      const flowExceeded = usedCapacityPercent >= 100;
      const capacityExceeded = flowExceeded;

      // Status based on percentage only
      let healthStatus;
      if (allSedimentFilters) {
        healthStatus = 'sediment';
      } else if (hasCapacityDataError) {
        healthStatus = 'dataError';
      } else if (capacityExceeded) {
        healthStatus = 'critical';
      } else if (remainingCapacityPercent <= 20) {
        healthStatus = 'critical';
      } else if (remainingCapacityPercent <= 40) {
        healthStatus = 'warning';
      } else {
        healthStatus = 'good';
      }

      const healthColor =
        healthStatus === 'sediment'
          ? Colors.gray8
          : healthStatus === 'dataError'
          ? Colors.gray8
          : healthStatus === 'good'
          ? Colors.green
          : healthStatus === 'warning'
          ? Colors.yellow
          : Colors.red;

      const pieData = [
        {
          name: 'Used',
          value: usedCapacityPercent,
        },
        {
          name: 'Remaining',
          value: remainingCapacityPercent > 0 ? remainingCapacityPercent : 0,
        },
      ];

      return (
        <div style={{ width: '100%' }}>
          <Row style={{ display: 'flex', width: '100%', margin: 0, flexWrap: 'wrap' }}>
            {/* Pie chart - full width on mobile, half width on larger screens */}
            <Col xs={100} sm={50} md={50} lg={50} style={{ padding: 0 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  padding: '10px',
                  alignItems: 'center',
                }}
              >
                {/* Pie Chart Container */}
                <div
                  style={{
                    width: '200px',
                    height: '200px',
                    position: 'relative',
                  }}
                >
                  {cartridgeTotalConsumptionWfLoading ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                      }}
                    >
                      <Loader size={30} />
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          startAngle={90}
                          endAngle={-270}
                          dataKey="value"
                          activeShape={props =>
                            this.renderActiveShape({ ...props, allSedimentFilters })
                          }
                          activeIndex={1}
                        >
                          {pieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={index === 0 ? Colors.gray3 : healthColor}
                            />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {!cartridgeAllTotalConsumptionWfLoading && (
                  <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <div
                      style={{
                        display: 'inline-block',
                        background: '#f5f5f5',
                        padding: '5px 10px',
                        borderRadius: '4px',
                      }}
                    >
                      {allSedimentFilters ? (
                        <H5 color={Colors.gray8} style={{ margin: 0, textAlign: 'center' }} />
                      ) : hasCapacityDataError ? (
                        <H5 color={Colors.gray8} style={{ margin: 0, textAlign: 'center' }}>
                          <span style={{ fontWeight: 'bold', color: Colors.gray8 }}>Error</span> in
                          capacity data
                        </H5>
                      ) : (
                        <H5 color={Colors.gray8} style={{ margin: 0, textAlign: 'center' }}>
                          <span
                            style={{
                              fontWeight: 'bold',
                              color: capacityExceeded
                                ? Colors.red
                                : healthColor === Colors.yellow
                                ? '#FFA000'
                                : healthColor,
                            }}
                          >
                            {remainingCapacityPercent}%
                          </span>{' '}
                          capacity remaining
                        </H5>
                      )}
                      <H5
                        color={Colors.gray8}
                        style={{
                          margin: '5px 0 0 0',
                          textAlign: 'center',
                          fontSize: '16px',
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 600,
                        }}
                      >
                        {cartridgeAllTotalConsumptionWf}{' '}
                        {allSedimentFilters
                          ? 'gal since last replaced'
                          : `/ ${totalCatridgeCapacity || '?'} gal`}
                      </H5>
                    </div>
                  </div>
                )}
              </div>
            </Col>
            {/* Filter status - full width on mobile, half width on larger screens */}
            <Col xs={100} sm={50} md={50} lg={50} style={{ padding: 0, marginTop: 15 }}>
              <div
                style={{
                  padding: '10px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                {cartridgeTotalConsumptionWfLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Loader size={30} />
                  </div>
                ) : (
                  <>
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '15px',
                          borderLeft: `4px solid ${
                            allSedimentFilters
                              ? Colors.gray8
                              : hasCapacityDataError
                              ? Colors.gray8
                              : capacityExceeded
                              ? Colors.red
                              : healthColor === Colors.yellow
                              ? '#FFA500'
                              : healthColor
                          }`,
                          paddingLeft: '10px',
                        }}
                      >
                        <H4 color={Colors.black} weight={500} style={{ margin: 0 }}>
                          Filter Status
                        </H4>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          marginBottom: '15px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <H5 color={Colors.gray8}>Last Replaced:</H5>
                          <H5 color={Colors.black} weight={500}>
                            {moment(firstCartridge.last_replaced).format('MMM D, YYYY')}
                          </H5>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <H5 color={Colors.gray8}>Installed For:</H5>
                          <H5 color={Colors.black} weight={500}>
                            {replacedWeeks} weeks
                          </H5>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <H5 color={Colors.gray8}>Capacity Remaining:</H5>
                          <H5
                            color={
                              allSedimentFilters
                                ? Colors.gray8
                                : hasCapacityDataError
                                ? Colors.gray8
                                : flowExceeded
                                ? Colors.red
                                : Colors.black
                            }
                            weight={500}
                          >
                            {allSedimentFilters
                              ? 'N/A'
                              : hasCapacityDataError
                              ? `${cartridgeAllTotalConsumptionWf} gal`
                              : `${remainingCapacityPercent}%`}
                          </H5>
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        background: allSedimentFilters
                          ? '#F5F5F5'
                          : hasCapacityDataError
                          ? '#F5F5F5'
                          : capacityExceeded
                          ? '#FFEBEE'
                          : healthColor === Colors.yellow
                          ? '#FFF8E1'
                          : healthColor === Colors.green
                          ? '#E8F5E9'
                          : '#FFEBEE',
                        padding: '10px',
                        borderRadius: '4px',
                        textAlign: 'center',
                      }}
                    >
                      <H3
                        color={
                          allSedimentFilters
                            ? Colors.gray8
                            : hasCapacityDataError
                            ? Colors.gray8
                            : capacityExceeded
                            ? Colors.red
                            : healthColor === Colors.yellow
                            ? '#FFA000'
                            : healthColor
                        }
                        weight={600}
                        style={{ margin: 0 }}
                      >
                        {allSedimentFilters
                          ? 'Sediment filters do not have a gallon capacity - exhaustion is based on pressure drop.'
                          : hasCapacityDataError
                          ? 'Data Error - Contact Support'
                          : capacityExceeded
                          ? 'Please Replace Filters'
                          : `${remainingCapacityPercent}% capacity remaining`}
                      </H3>
                    </div>
                  </>
                )}
              </div>
            </Col>
          </Row>

          {/* Filter Cartridges List */}
          {flowMeters && flowMeters.length > 0 && (
            <Row style={{ marginTop: '15px', width: '100%', margin: '15px 0 0 0' }}>
              <Col xs={100} sm={100} md={100} lg={100} style={{ padding: 0 }}>
                <LineSeparator style={{ marginBottom: '10px' }} />
                <H4 color={Colors.black} weight={500} style={{ marginBottom: '15px' }}>
                  Installed Cartridges
                </H4>
                <Row
                  style={{
                    width: '100%',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    margin: 0,
                  }}
                >
                  {flowMeters.map((meter, index) => {
                    // Calculate column width based on number of filters to ensure even spacing
                    const colWidth =
                      flowMeters.length <= 3 ? Math.floor(100 / flowMeters.length) : 33;
                    const isSediment = meter.SpotsWaterFilter.Catridge.sediment === true;

                    return (
                      <Col
                        key={index}
                        xs={100}
                        sm={50}
                        md={colWidth}
                        lg={colWidth}
                        style={{
                          marginBottom: 16,
                          paddingLeft: 8,
                          paddingRight: 8,
                        }}
                      >
                        <div
                          style={{
                            minHeight: 95,
                            width: '100%',
                            padding: '15px',
                            backgroundColor: '#f8fbff',
                            borderRadius: '8px',
                            border: '1px solid #edf2f7',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            position: 'relative',
                          }}
                        >
                          {isSediment && (
                            <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                              <a
                                data-tooltip-id={`sediment-tooltip-${index}`}
                                data-tooltip-content="Sediment filters (SED) do not have a chlorine reduction gallon capacity."
                              >
                                <Icon name="info-circle" color={Colors.gray8} fontSize={14} />
                              </a>
                              <TooltipReact id={`sediment-tooltip-${index}`} place="top" />
                            </div>
                          )}
                          <div style={{ marginRight: '15px' }}>
                            {capacityExceeded || healthStatus === 'critical' ? (
                              <Icon name="exclamation-circle" color={Colors.red} fontSize={30} />
                            ) : (
                              <Icon name="check-circle" color={Colors.green} fontSize={30} />
                            )}
                          </div>
                          <div>
                            <H4
                              style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: 500,
                                color: '#333',
                                marginBottom: 5,
                                textAlign: 'left',
                              }}
                            >
                              {meter.SpotsWaterFilter.Catridge.name}
                            </H4>
                            <H5
                              style={{
                                margin: 0,
                                color: Colors.gray8,
                                fontSize: '12px',
                                textAlign: 'left',
                              }}
                            >
                              {meter.SpotsWaterFilter.position} {isSediment && '(Sediment)'}
                            </H5>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Col>
            </Row>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          padding: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '180px',
          background: '#f9f9f9',
          borderRadius: '4px',
        }}
      >
        <ListEmpty message="The cartridges have not been replaced yet" />
      </div>
    );
  };

  renderConfigurationModal = () => {
    const { showConfiguration } = this.state;
    const {
      location: {
        WaterFilterHead: {
          WaterFilterConfiguration,
          chlorine_or_chloramine,
          FlowMeterOfWaterFilters,
        },
      },
    } = this.props;
    const { name } = WaterFilterConfiguration;

    return (
      <Modal
        title="Configuration"
        onClose={() => this.switchModal('showConfiguration')}
        isVisible={showConfiguration}
      >
        <H4 lineHeight={2} textAlign="left" color={Colors.gray8} weight={325} style={{ margin: 0 }}>
          Configuration name
        </H4>
        <H4 lineHeight={2} color={Colors.black} weight={500} style={{ marginTop: 5 }}>
          {name}
        </H4>
        <H4 lineHeight={2} color={Colors.black} weight={500} style={{ marginTop: 5 }}>
          <Icon name="check-circle" color={Colors.lightBlue} customStyle={{ marginRight: 5 }} />
          {chlorine_or_chloramine}
        </H4>

        {FlowMeterOfWaterFilters.map(({ SpotsWaterFilter, type, name }) => {
          const { WaterFilter, Catridge } = SpotsWaterFilter;
          return (
            <>
              <H4 lineHeight={2} color={Colors.lightBlue} weight="bold" style={{ marginTop: 5 }}>
                {WaterFilter.name}
              </H4>

              <Row alignItems="left" lg={100} md={100} sm={100} xs={100}>
                <Col lg={30} md={30} sm={30} xs={30} alignItems="left">
                  <H4 lineHeight={2} weight="bold" style={{ marginTop: 5 }}>
                    {SpotsWaterFilter.position} -{Catridge.name}
                  </H4>
                </Col>
                <Col lg={70} md={70} sm={70} xs={70} alignItems="left">
                  <H4 lineHeight={2} weight="bold" style={{ marginTop: 5 }}>
                    Capacity: {this.calculeCatridgeCapacity(Catridge, chlorine_or_chloramine)} gal
                  </H4>
                </Col>
                <>
                  <Col lg={30} md={30} sm={30} xs={30} alignItems="left">
                    <H4
                      lineHeight={2}
                      textAlign="left"
                      color={Colors.gray8}
                      weight={325}
                      style={{ margin: 0 }}
                    >
                      Type
                    </H4>

                    <H4 lineHeight={2} weight="bold" style={{ marginTop: 5 }}>
                      {type}
                    </H4>
                  </Col>
                  <Col lg={70} md={70} sm={70} xs={70} alignItems="left">
                    <H4
                      lineHeight={2}
                      textAlign="left"
                      color={Colors.gray8}
                      weight={325}
                      style={{ margin: 0 }}
                    >
                      Name
                    </H4>
                    <H4 lineHeight={2} weight="bold" style={{ marginTop: 5 }}>
                      {name}
                    </H4>
                  </Col>

                  {Catridge.photo && (
                    <Col alignItems="start" xs={100} md={100} sm={100} lg={100}>
                      <img width={200} src={Catridge.photo} />
                    </Col>
                  )}
                  <LineSeparator />
                </>
              </Row>
            </>
          );
        })}
      </Modal>
    );
  };

  renderBypassConsumptions = () => {
    const {
      waterFilterBypass: { consumption = [], total_flow = 0, last_updated },
      waterFilterBypassLoading,
      location: {
        WaterFilterHead: { device_id },
      },
    } = this.props;
    const { datePickerFromBypass, datePickerToBypass, bypassInterval } = this.state;

    const displayTotalFlow =
      total_flow && !Number.isNaN(parseFloat(total_flow)) && parseFloat(total_flow) > 0
        ? Math.round(parseFloat(total_flow))
        : null;

    return (
      <GraphContainer height={540}>
        <Col style={{ width: '100%', alignItems: 'stretch' }}>
          <Row
            justifyContent="space-between"
            alignItems="center"
            style={{ width: '100%', marginBottom: 20 }}
          >
            <H2
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: '24px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Bypass Flow
            </H2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <IntervalBadge>{getIntervalLabel(bypassInterval)}</IntervalBadge>
              <DateRangeButton
                type="button"
                onClick={() => this.switchModal('modalDatePickerBypass')}
              >
                {moment(datePickerFromBypass).format('MM/DD/YYYY')} –{' '}
                {moment(datePickerToBypass).format('MM/DD/YYYY')}
                <Icon name="calendar-alt" color={Colors.lightBlue} fontSize={14} />
              </DateRangeButton>
            </div>
          </Row>

          <ChartSection style={{ alignItems: 'stretch' }}>
            {!device_id ? (
              <Col style={{ height: 300 }} justifyContent="center">
                <ListEmpty message="Installation Not Complete" />
              </Col>
            ) : (
              <ChartViewportFull>
                <FlowVolumeChart
                  id="bypassFlowChart"
                  dataChart={consumption}
                  consumption={consumption}
                  loading={waterFilterBypassLoading}
                  pressureLoading={false}
                  showPressure={false}
                />
              </ChartViewportFull>
            )}
            {last_updated && !waterFilterBypassLoading && (
              <ChartMeta>
                <LastUpdatedBadge>
                  <LastUpdatedLabel>Updated</LastUpdatedLabel>
                  <LastUpdatedValue>{moment.unix(last_updated).format('h:mm A')}</LastUpdatedValue>
                  <LastUpdatedDot>·</LastUpdatedDot>
                  <LastUpdatedValue>
                    {moment.unix(last_updated).format('MMM D, YYYY')}
                  </LastUpdatedValue>
                </LastUpdatedBadge>
              </ChartMeta>
            )}
          </ChartSection>

          <StatsRow style={{ gridTemplateColumns: '1fr', maxWidth: '320px' }}>
            <StatCard>
              <StatLabel>Total Flow</StatLabel>
              <div style={{ marginTop: 10 }}>
                {waterFilterBypassLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Loader size={15} />
                    <H5 color={Colors.gray8} style={{ marginLeft: 10, margin: 0 }}>
                      Loading...
                    </H5>
                  </div>
                ) : (
                  <>
                    <H5 weight="bold" color={Colors.lightBlue} marginTop={5} style={{ margin: 0 }}>
                      since installation
                    </H5>
                    <div style={{ marginTop: 8 }}>
                      <StatValue>{displayTotalFlow == null ? 'N/A' : displayTotalFlow}</StatValue>
                      {displayTotalFlow != null && <StatUnit>gal</StatUnit>}
                    </div>
                  </>
                )}
              </div>
            </StatCard>
          </StatsRow>
        </Col>
      </GraphContainer>
    );
  };

  renderFilterConsumptions = () => {
    const {
      waterFilterConsumption: { consumption, last_updated, total_flow },
      waterFilterPressureForConsumption: { pressures },
      waterFilterConsumptionLoading,
      waterFilterPressureForConsumptionLoading,
      location: {
        WaterFilterHead: { device_id },
      },
      totalConsumptionWaterFilterLoading,
      waterFilterTotalConsumption,
    } = this.props;
    const { datePickerFromConsumption, datePickerToConsumption, consumptionInterval } = this.state;

    const dataChart = mergeChartSeries(consumption, pressures);

    return (
      <GraphContainer height={540}>
        <Col style={{ width: '100%', alignItems: 'stretch' }}>
          {/* Flow Volume Header Row */}
          <Row
            justifyContent="space-between"
            alignItems="center"
            style={{ width: '100%', marginBottom: 20 }}
          >
            <H2
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: '24px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Flow Volume
            </H2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <IntervalBadge>{getIntervalLabel(consumptionInterval)}</IntervalBadge>
              <DateRangeButton
                type="button"
                onClick={() => this.switchModal('modalDatePickerConsumption')}
              >
                {moment(datePickerFromConsumption).format('MM/DD/YYYY')} –{' '}
                {moment(datePickerToConsumption).format('MM/DD/YYYY')}
                <Icon name="calendar-alt" color={Colors.lightBlue} fontSize={14} />
              </DateRangeButton>

              {/* Info tooltip */}
              <div style={{ marginLeft: 10 }}>
                <a
                  data-tooltip-id="flow-volume-tooltip"
                  data-tooltip-html="<strong>Flow Volume Graph</strong> <br>Displays total flow volume by time period."
                >
                  <Icon name="info-circle" color={Colors.gray8} fontSize={16} />
                </a>
                <TooltipReact id="flow-volume-tooltip" />
              </div>
            </div>
          </Row>

          <ChartSection>
            {!device_id ? (
              <Col style={{ height: 300 }} justifyContent="center">
                <ListEmpty message="Installation Not Complete" />
              </Col>
            ) : (
              <ChartViewport>
                <FlowVolumeChart
                  id="totalGaloonsChart"
                  dataChart={dataChart}
                  consumption={consumption}
                  loading={waterFilterConsumptionLoading}
                  pressureLoading={waterFilterPressureForConsumptionLoading}
                />
              </ChartViewport>
            )}
            {last_updated && !waterFilterConsumptionLoading && (
              <ChartMeta>
                <LastUpdatedBadge>
                  <LastUpdatedLabel>Updated</LastUpdatedLabel>
                  <LastUpdatedValue>{moment.unix(last_updated).format('h:mm A')}</LastUpdatedValue>
                  <LastUpdatedDot>·</LastUpdatedDot>
                  <LastUpdatedValue>
                    {moment.unix(last_updated).format('MMM D, YYYY')}
                  </LastUpdatedValue>
                </LastUpdatedBadge>
              </ChartMeta>
            )}
          </ChartSection>

          <StatsRow>
            <StatCard>
              <StatLabel>Flow for Selected Period</StatLabel>
              <div style={{ marginTop: 10 }}>
                {waterFilterConsumptionLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Loader size={15} />
                    <H5 color={Colors.gray8} style={{ marginLeft: 10, margin: 0 }}>
                      Loading...
                    </H5>
                  </div>
                ) : (
                  <div>
                    <StatValue>
                      {total_flow === 0 ? 'N/A' : Math.round(total_flow).toLocaleString()}
                    </StatValue>
                    {total_flow !== 0 && <StatUnit>gal</StatUnit>}
                  </div>
                )}
              </div>
            </StatCard>

            <StatCard>
              <StatLabel>Total Flow Since Installation</StatLabel>
              <div style={{ marginTop: 10 }}>
                {totalConsumptionWaterFilterLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Loader size={15} />
                    <H5 color={Colors.gray8} style={{ marginLeft: 10, margin: 0 }}>
                      Loading...
                    </H5>
                  </div>
                ) : (
                  <div>
                    <StatValue>
                      {waterFilterTotalConsumption === 0
                        ? 'N/A'
                        : Math.round(waterFilterTotalConsumption).toLocaleString()}
                    </StatValue>
                    {waterFilterTotalConsumption !== 0 && <StatUnit>gal</StatUnit>}
                  </div>
                )}
              </div>
            </StatCard>
          </StatsRow>
        </Col>
      </GraphContainer>
    );
  };

  renderMaxFlowAndPressures = () => {
    const {
      waterFilterMaxFlowRate: { consumption },
      waterFilterPressureForFlowRate: { pressures },
      waterFilterMaxFlowRateLoading,
      waterFilterPressureForFlowRateLoading,
      location: {
        WaterFilterHead: { device_id },
      },
    } = this.props;

    const { datePickerFromMaxFlowRate, datePickerToMaxFlowRate, maxFlowRateInterval } = this.state;

    const dataChart = mergeChartSeries(consumption, pressures);

    return (
      <GraphContainer height={420}>
        <Col style={{ width: '100%', alignItems: 'stretch' }}>
          <Row
            justifyContent="space-between"
            alignItems="center"
            style={{ width: '100%', marginBottom: 20 }}
          >
            <H2
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: '24px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              Max Pressure Differential & Flow Rate
            </H2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <IntervalBadge>{getIntervalLabel(maxFlowRateInterval)}</IntervalBadge>
              <DateRangeButton
                type="button"
                onClick={() => this.switchModal('modalDatePickerMaxFlowRate')}
              >
                {moment(datePickerFromMaxFlowRate).format('MM/DD/YYYY')} –{' '}
                {moment(datePickerToMaxFlowRate).format('MM/DD/YYYY')}
                <Icon name="calendar-alt" color={Colors.lightBlue} fontSize={14} />
              </DateRangeButton>

              <div style={{ marginLeft: 10 }}>
                <a
                  data-tooltip-id="max-flow-pressure-tooltip"
                  data-tooltip-html="<strong>Max Pressure Differential &amp; Flow Rate</strong><br>Daily peak flow rate (GPM) and max pressure differential per filter.<br>A clog alert is sent when the live pressure drop is 25 PSI or more. Cartridge Max Pressure is the plumbing rating, not this threshold."
                >
                  <Icon name="info-circle" color={Colors.gray8} fontSize={16} />
                </a>
                <TooltipReact id="max-flow-pressure-tooltip" />
              </div>
            </div>
          </Row>

          <ChartSection style={{ alignItems: 'stretch', width: '100%' }}>
            {!device_id ? (
              <Col style={{ height: 300 }} justifyContent="center">
                <ListEmpty message="Installation Not Complete" />
              </Col>
            ) : (
              <ChartViewportFull>
                <MaxFlowPressureChart
                  id="maxFlowAndPressureChart"
                  dataChart={dataChart}
                  loading={waterFilterMaxFlowRateLoading}
                  pressureLoading={waterFilterPressureForFlowRateLoading}
                />
              </ChartViewportFull>
            )}
          </ChartSection>
        </Col>
      </GraphContainer>
    );
  };

  switchModal = modalName => {
    this.setState(prevState => ({ [modalName]: !prevState[modalName] }));
  };

  handleChangeDeviceId = evt => {
    this.setState({ new_device_id: evt.target.value });
  };

  generateWaterFilterCsv = email => {
    const {
      generateWaterFilterCsv,
      location: {
        id,
        WaterFilterHead: { device_id },
      },
    } = this.props;
    const { csvTypeString } = this.state;
    const { datePickerFromCsv: from, datePickerToCsv: to } = this.state;
    generateWaterFilterCsv({
      device_id,
      location_id: id,
      from,
      to,
      csvContains: csvTypeString,
      email,
    });
  };

  changeCsvType = (csvType, nro) => {
    const csvTypes = ['raw_data', 'calculated_data', 'all_data'];

    this.setState({ [csvType]: nro, csvTypeString: csvTypes[nro - 1] });
  };

  calculeWaterFilterConsumption = (range, wf_for_pfd) => {
    const {
      getWaterFilterConsumption,
      getWaterFilterPressureForConsumption,
      location: {
        WaterFilterHead: { device_id },
      },
    } = this.props;
    const {
      datePickerFromConsumption: from,
      datePickerToConsumption: to,
      consumptionInterval,
    } = this.state;

    // Calculate possible ranges but default to 'days'
    const possibleDayRangesConsumption = this.calculeDaysRange(from, to);

    // If no specific range is provided or the provided range is not available, use 'days'
    const currentRange = range || 'days';

    // Call API with the determined range
    getWaterFilterConsumption({
      device_id,
      from,
      to,
      interval: consumptionInterval,
      range: currentRange,
      wf_for_pfd,
    });
    getWaterFilterPressureForConsumption({
      device_id,
      from,
      to,
      interval: consumptionInterval,
      range: currentRange,
      wf_for_pfd,
    });

    this.setState({ possibleDayRangesConsumption, range: currentRange });
  };

  calculeWaterFilterMaxFlowRate = (range, wf_for_pfd) => {
    const {
      getWaterFilterMaxFlowRate,
      getWaterFilterPressureForFlowRate,
      location: {
        WaterFilterHead: { device_id },
      },
    } = this.props;
    const {
      datePickerFromMaxFlowRate: from,
      datePickerToMaxFlowRate: to,
      maxFlowRateInterval,
    } = this.state;

    const possibleDayRangesMaxFlowRate = this.calculeDaysRange(from, to);
    const currentRange = range || 'days';

    getWaterFilterMaxFlowRate({
      device_id,
      from,
      to,
      interval: maxFlowRateInterval,
      range: currentRange,
      wf_for_pfd,
    });
    getWaterFilterPressureForFlowRate({
      device_id,
      from,
      to,
      interval: maxFlowRateInterval,
      range: currentRange,
      wf_for_pfd,
    });

    this.setState({ possibleDayRangesMaxFlowRate, range: currentRange });
  };

  calculeWaterFilterBypass = () => {
    const {
      getWaterFilterBypass,
      location: {
        WaterFilterHead: { device_id, installed_date },
      },
    } = this.props;
    const { datePickerFromBypass: from, datePickerToBypass: to, bypassInterval } = this.state;

    getWaterFilterBypass({
      device_id,
      from,
      to,
      installed_date,
      interval: bypassInterval,
    });
  };

  calculeDaysRange = (from, to) => {
    const possibleDayRanges = [];
    const fromDate = moment(from);
    const toDate = moment(to);
    const daysRange = toDate.diff(fromDate, 'days');

    // The maximum of days chosen in each condition is the equivalent of the maximum of 15 bars on the chart
    if (daysRange <= 1) possibleDayRanges.push('hours');
    if (daysRange >= 1 && daysRange < 15) possibleDayRanges.push('days');
    if (daysRange >= 14 && daysRange < 105) possibleDayRanges.push('weeks');
    if (daysRange >= 30 && daysRange < 450) possibleDayRanges.push('months');
    if (daysRange >= 365) possibleDayRanges.push('years');
    return possibleDayRanges;
  };

  replaceWaterFilterCartridgesFunc = () => {
    const {
      location: {
        WaterFilterHead: { id: water_filter_head_id },
      },
      replaceWaterFilterCartridges,
    } = this.props;

    const { cartridgesChangeDate } = this.state;
    const cartridgesChangeDateUTC = moment(cartridgesChangeDate)
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    replaceWaterFilterCartridges({ water_filter_head_id, new_date: cartridgesChangeDateUTC });
  };

  handleDate = (value, key) => {
    this.setState({ [key]: value });
  };

  generateReport = async () => {
    try {
      const { datePickerFromReport, datePickerToReport } = this.state;

      this.setState(
        {
          datePickerFromConsumption: datePickerFromReport,
          datePickerToConsumption: datePickerToReport,
          datePickerFromMaxFlowRate: datePickerFromReport,
          datePickerToMaxFlowRate: datePickerToReport,
          generatePDF: true,
        },
        () => {
          this.calculeWaterFilterConsumption(false, true);
          this.calculeWaterFilterMaxFlowRate(false, true);
        },
      );
    } catch (error) {
      this.setState({ generatePDF: false });
      console.log(error);
    }
  };

  downloadPDF = () => {
    const {
      totalGaloonsChart,
      exhaustionChart,
      maxFlowAndPressureChart,
      downloadPDF,
      datePickerFromConsumption,
      datePickerToConsumption,
      catridgeSelectedObjet,
      catridgeCapacity,
      totalCatridgeCapacity,
    } = this.state;
    const {
      location,
      allAlerts,
      waterFilterConsumption,
      waterFilterTotalConsumption,
      waterFilterCurrentPressure,
      cartridgeTotalConsumptionWf,
      cartridgeAllTotalConsumptionWf,
      waterFilterCurrentBypass,
    } = this.props;

    if (downloadPDF) {
      return (
        <PDFDownloadLink
          document={
            <ReportWF
              datePickerFrom={datePickerFromConsumption}
              datePickerTo={datePickerToConsumption}
              totalGaloonsChart={totalGaloonsChart}
              exhaustionChart={exhaustionChart}
              maxFlowAndPressureChart={maxFlowAndPressureChart}
              location={location}
              allAlerts={allAlerts}
              waterFilterConsumption={waterFilterConsumption}
              waterFilterTotalConsumption={waterFilterTotalConsumption}
              catridgeSelectedObjet={catridgeSelectedObjet}
              cartridgeTotalConsumptionWf={cartridgeTotalConsumptionWf}
              cartridgeAllTotalConsumptionWf={cartridgeAllTotalConsumptionWf}
              totalCatridgeCapacity={totalCatridgeCapacity}
              pressures={waterFilterCurrentPressure}
              catridgeCapacity={catridgeCapacity}
              waterFilterCurrentBypass={waterFilterCurrentBypass}
            />
          }
          fileName="ReportWF.pdf"
        >
          {({ loading }) => (
            <H4
              color={Colors.lightBlue}
              style={{ cursor: 'pointer' }}
              onClick={() => this.setState({ generatePDF: false, downloadPDF: false })}
            >
              <Icon
                name={loading ? 'far fa-spinner' : 'far fa-file-download'}
                color={Colors.lightBlue}
                customStyle={{ marginRight: '5px' }}
              />
              {loading ? 'Loading' : 'Download'} Report
            </H4>
          )}
        </PDFDownloadLink>
      );
    }
    return (
      <Row>
        <Loader size={35} block />
        <H4 color={Colors.lightBlue} marginTop={5}>
          Generating PDF ...
        </H4>
      </Row>
    );
  };

  // Add this method after the calculeWaterFilterBypass method
  switchToPressureDetail = () => {
    this.setState({ modalPressureDetail: true });
  };

  // Add this method to render detailed pressure information for all filters
  renderPressureDetailModal = () => {
    const {
      waterFilterCurrentPressure,
      waterFilterCurrentPressureLoading,
      location: {
        WaterFilterHead: { device_id },
      },
    } = this.props;

    const { modalPressureDetail } = this.state;

    return (
      <Modal
        title="Filter Pressure Details"
        onClose={() => this.setState({ modalPressureDetail: false })}
        isVisible={modalPressureDetail}
        size="medium"
      >
        {waterFilterCurrentPressureLoading ? (
          <GraphContainer height={300}>
            <Loader size={120} block />
          </GraphContainer>
        ) : !device_id ? (
          <Col justifyContent="center">
            <ListEmpty message="Installation Not Complete" />
          </Col>
        ) : (
          <div style={{ padding: '10px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #ECEBF0',
                      textAlign: 'left',
                    }}
                  >
                    Filter
                  </th>
                  <th
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #ECEBF0',
                      textAlign: 'center',
                    }}
                  >
                    Inlet (PSI)
                  </th>
                  <th
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #ECEBF0',
                      textAlign: 'center',
                    }}
                  >
                    Outlet (PSI)
                  </th>
                  <th
                    style={{
                      padding: '10px',
                      borderBottom: '1px solid #ECEBF0',
                      textAlign: 'center',
                    }}
                  >
                    Differential (PSI)
                  </th>
                </tr>
              </thead>
              <tbody>
                {waterFilterCurrentPressure.map((pressure, index) => {
                  const inletPressure =
                    pressure &&
                    pressure.inletPressure !== undefined &&
                    pressure.inletPressure !== null &&
                    !isNaN(parseFloat(pressure.inletPressure))
                      ? parseFloat(pressure.inletPressure)
                      : null;

                  const outletPressure =
                    pressure &&
                    pressure.outletPressure !== undefined &&
                    pressure.outletPressure !== null &&
                    !isNaN(parseFloat(pressure.outletPressure))
                      ? parseFloat(pressure.outletPressure)
                      : null;

                  const differential =
                    inletPressure !== null && outletPressure !== null
                      ? Math.max(0, inletPressure - outletPressure)
                      : null;

                  return (
                    <tr key={index}>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #ECEBF0',
                          fontWeight: 500,
                        }}
                      >
                        Filter {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #ECEBF0',
                          textAlign: 'center',
                          color: Colors.blue,
                        }}
                      >
                        {inletPressure !== null ? Math.round(inletPressure) : 'N/A'}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #ECEBF0',
                          textAlign: 'center',
                          color: Colors.blue,
                        }}
                      >
                        {outletPressure !== null ? Math.round(outletPressure) : 'N/A'}
                      </td>
                      <td
                        style={{
                          padding: '12px',
                          borderBottom: '1px solid #ECEBF0',
                          textAlign: 'center',
                          color:
                            differential !== null && differential >= 25 ? Colors.red : Colors.blue,
                        }}
                      >
                        {differential !== null ? Math.round(differential) : 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div
              style={{
                marginTop: '15px',
                padding: '10px',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
              }}
            >
              <H5 color={Colors.gray8}>
                <span style={{ color: Colors.red, fontWeight: 500 }}>Note:</span> A differential
                pressure of 25 PSI or more may indicate a clogged filter that needs replacement.
                Cartridge Max Pressure is a plumbing rating and is not used for this alert.
              </H5>
            </div>
          </div>
        )}
      </Modal>
    );
  };

  render() {
    /* eslint-disable no-unused-vars */
    const {
      location: { WaterFilterHead, id: location_id },
      waterFilterConsumption,
      waterFilterConsumptionLoading,
      totalConsumptionWaterFilterLoading,
      waterFiltercurrentStateLoading,
      cartridgeTotalConsumptionWfLoading,
      setDeviceId,
      waterFilterCurrentPressure,
      waterFilterCurrentPressureLoading,
      waterFilterCurrentBypass,
      waterFilterCurrentBypassLoading,
      waterFilterPressureForConsumptionLoading,
      waterFilterPressureForFlowRateLoading,
      renderInstructionsModal,
      waterFilterCurrentState,
    } = this.props;

    const {
      cartridgesChangeDate,
      modalDatePickerConsumption,
      modalCartridges,
      datePickerFromConsumption,
      datePickerToConsumption,
      consumptionInterval,
      maxFlowRateInterval,
      catridgeSelectedObjet,
      modalDatePickerCsv,
      datePickerFromCsv,
      datePickerToCsv,
      csvType,
      new_device_id,
      modalDeviceId,
      modalBypass,
      datePickerFromBypass,
      datePickerToBypass,
      modalDatePickerBypass,
      bypassInterval,
      datePickerFromReport,
      datePickerToReport,
      modalDatePickerReport,
      modalDatePickerMaxFlowRate,
      datePickerFromMaxFlowRate,
      datePickerToMaxFlowRate,
    } = this.state;
    /* eslint-enable no-unused-vars */

    const {
      WaterFilterConfiguration,
      FlowMeterOfWaterFilters,
      device_id,
      installed_date,
    } = WaterFilterHead;

    const { WatherFilterInstallSteps } = WaterFilterConfiguration || {};
    const cartridgesOfWFChanges = FlowMeterOfWaterFilters?.[0]?.CartridgesOfWFChanges;

    // Calculate average pressures across filters
    let avgInletPressure = 0;
    let avgOutletPressure = 0;
    let avgDifferentialPressure = 0;
    let multipleFilters = false;

    if (waterFilterCurrentPressure && waterFilterCurrentPressure.length > 0) {
      multipleFilters = waterFilterCurrentPressure.length > 1;

      // Count only valid filter values
      let validInletFilters = 0;
      let validOutletFilters = 0;
      let validDiffFilters = 0;

      // Calculate sums with validation
      const inletSum = waterFilterCurrentPressure.reduce((sum, filter) => {
        // Check if the value is a number and not NaN/undefined/null
        if (
          filter &&
          filter.inletPressure !== undefined &&
          filter.inletPressure !== null &&
          !isNaN(parseFloat(filter.inletPressure))
        ) {
          validInletFilters++;
          return sum + parseFloat(filter.inletPressure);
        }
        return sum;
      }, 0);

      const outletSum = waterFilterCurrentPressure.reduce((sum, filter) => {
        if (
          filter &&
          filter.outletPressure !== undefined &&
          filter.outletPressure !== null &&
          !isNaN(parseFloat(filter.outletPressure))
        ) {
          validOutletFilters++;
          return sum + parseFloat(filter.outletPressure);
        }
        return sum;
      }, 0);

      const diffSum = waterFilterCurrentPressure.reduce((sum, filter) => {
        if (
          filter &&
          filter.inletPressure !== undefined &&
          filter.inletPressure !== null &&
          !isNaN(parseFloat(filter.inletPressure)) &&
          filter.outletPressure !== undefined &&
          filter.outletPressure !== null &&
          !isNaN(parseFloat(filter.outletPressure))
        ) {
          validDiffFilters++;
          const diff = Math.max(
            0,
            parseFloat(filter.inletPressure) - parseFloat(filter.outletPressure),
          );
          return sum + diff;
        }
        return sum;
      }, 0);

      // Calculate averages only if we have valid data
      avgInletPressure = validInletFilters > 0 ? inletSum / validInletFilters : 0;
      avgOutletPressure = validOutletFilters > 0 ? outletSum / validOutletFilters : 0;
      avgDifferentialPressure = validDiffFilters > 0 ? diffSum / validDiffFilters : 0;
    }

    // Format bypass status - check both waterFilterCurrentBypass and flow_3 values
    const flow3Value = waterFilterCurrentState?.flow_3 || 0;

    const bypassStatus = Number(waterFilterCurrentBypass) > 0 || Number(flow3Value) > 0;
    const bypassStatusText = bypassStatus ? 'On' : 'Off';

    return (
      <Container>
        <Row>
          <Col lg={50} md={100} sm={100} xs={100}>
            <BG>{this.renderFilterConsumptions(catridgeSelectedObjet)}</BG>
          </Col>

          <Col lg={50} md={100} sm={100} xs={100}>
            <BG>
              <GraphContainer height={540}>
                <Col>
                  {/* Filter Capacities Header Row */}
                  <Row
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ marginBottom: 20 }}
                  >
                    <H2
                      style={{
                        margin: 0,
                        fontWeight: 600,
                        fontSize: '24px',
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      Filter Capacities
                    </H2>
                    <div
                      style={{
                        backgroundColor: '#1498CB',
                        borderRadius: '5px',
                        padding: '8px 16px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                      }}
                      onClick={() => {
                        this.setState({ cartridgesChangeDate: new Date() });
                        this.switchModal('modalCartridges');
                      }}
                      onMouseOver={e => (e.currentTarget.style.backgroundColor = '#0e80ad')}
                      onMouseOut={e => (e.currentTarget.style.backgroundColor = '#1498CB')}
                      onFocus={e => (e.currentTarget.style.backgroundColor = '#0e80ad')}
                      onBlur={e => (e.currentTarget.style.backgroundColor = '#1498CB')}
                    >
                      <Icon
                        name="sync"
                        color="#FFFFFF"
                        fontSize={16}
                        customStyle={{ marginRight: '8px' }}
                      />
                      <H5 color="#FFFFFF" style={{ margin: 0, cursor: 'pointer' }}>
                        Replace Cartridges
                      </H5>
                    </div>
                  </Row>

                  {/* Filter Cartridges Content */}
                  <Col lg={100} md={100} sm={100} xs={100} style={{ width: '100%', padding: 0 }}>
                    {this.renderFilterCartridges(FlowMeterOfWaterFilters)}
                  </Col>
                </Col>
              </GraphContainer>
            </BG>
          </Col>
        </Row>

        {/* System Stats Section Header */}
        <Row
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            textAlign: 'left',
            marginLeft: 0,
            marginRight: 0,
          }}
        >
          <Col
            lg={100}
            md={100}
            sm={100}
            xs={100}
            style={{
              marginTop: 24,
              marginBottom: 8,
              padding: '0 8px',
              textAlign: 'left',
              alignItems: 'flex-start',
            }}
          >
            <H2
              style={{
                margin: 0,
                fontWeight: 600,
                fontSize: '24px',
                fontFamily: 'Inter, sans-serif',
                textAlign: 'left',
                width: '100%',
                display: 'block',
              }}
            >
              System Stats
            </H2>
          </Col>
        </Row>

        {/* Quick Stats Row */}
        <Row style={{ marginTop: 16, marginBottom: 24 }}>
          {/* Connected pressure cards - stack vertically on mobile */}
          <Col
            lg={75}
            md={100}
            sm={100}
            xs={100}
            style={{ padding: '0 8px', marginBottom: 16, display: 'flex' }}
          >
            <PressureStatsGrid>
              <QuickStats
                icon="tachometer-alt"
                title="Inlet Pressure"
                value={!isNaN(avgInletPressure) ? Math.round(avgInletPressure) : 0}
                unit="PSI"
                isLoading={waterFilterCurrentPressureLoading}
                isInstalled={!!device_id}
              />
              <QuickStats
                icon="tachometer-alt"
                title="Outlet Pressure"
                value={!isNaN(avgOutletPressure) ? Math.round(avgOutletPressure) : 0}
                unit="PSI"
                isLoading={waterFilterCurrentPressureLoading}
                isInstalled={!!device_id}
              />
              <QuickStats
                icon="tachometer-alt"
                title="Differential Pressure"
                value={!isNaN(avgDifferentialPressure) ? Math.round(avgDifferentialPressure) : 0}
                unit="PSI"
                isLoading={waterFilterCurrentPressureLoading}
                isInstalled={!!device_id}
                onClick={multipleFilters ? this.switchToPressureDetail : undefined}
              />
            </PressureStatsGrid>
          </Col>

          {/* Bypass status */}
          <Col
            lg={25}
            md={100}
            sm={100}
            xs={100}
            style={{ padding: '0 8px', marginBottom: 40, display: 'flex' }}
          >
            <div style={{ width: '100%' }}>
              <QuickStats
                icon="exchange-alt"
                iconColor={bypassStatus ? Colors.red : Colors.green}
                iconBgColor={bypassStatus ? 'rgba(235, 87, 87, 0.1)' : 'rgba(39, 174, 96, 0.1)'}
                title="Bypass Status"
                value={bypassStatusText}
                valueColor={bypassStatus ? Colors.red : Colors.green}
                isLoading={waterFilterCurrentBypassLoading}
                isInstalled={!!device_id}
                onClick={() => {
                  this.switchModal('modalBypass');
                  this.calculeWaterFilterBypass();
                }}
                height="120px"
              />
            </div>
          </Col>
        </Row>

        <Row style={{ marginBottom: 5, marginTop: 5 }}>
          <Col lg={100} md={100} sm={100} xs={100}>
            <BG>{this.renderMaxFlowAndPressures()}</BG>
          </Col>
        </Row>

        {this.renderConfigurationModal()}
        {this.renderPressureDetailModal()}

        <ModalCsvFiles
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerCsv}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromCsv')}
          handleDateTo={value => this.handleDate(value, 'datePickerToCsv')}
          switchModal={() => this.switchModal('modalDatePickerCsv')}
          dateFrom={datePickerFromCsv}
          dateTo={datePickerToCsv}
          changeCsvType={this.changeCsvType}
          csvType={csvType}
          userEmail={this.props.userEmail}
          saveChanges={email => {
            this.switchModal('modalDatePickerCsv');
            this.generateWaterFilterCsv(email);
          }}
        />

        <Modal
          title="Replace Cartridges"
          onClose={() => this.switchModal('modalCartridges')}
          isVisible={modalCartridges}
          size="small"
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <H4 color={Colors.gray4}>Changed date</H4>
            <DatePickerComponent
              minDate={moment(installed_date).toDate()}
              date={cartridgesChangeDate}
              onChange={value => this.handleDate(value, 'cartridgesChangeDate')}
              style={{ width: '100%' }}
            />
          </div>
          <Row
            style={{
              marginTop: 30,
              marginBottom: 10,
              gap: 10,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <ButtonText
              text="Cancel"
              theme="secondary"
              type="submit"
              onClick={() => this.switchModal('modalCartridges')}
            />
            <ButtonText
              text="Confirm"
              theme="primary"
              type="submit"
              onClick={() => {
                this.replaceWaterFilterCartridgesFunc();
                this.switchModal('modalCartridges');
              }}
            />
          </Row>

          {cartridgesOfWFChanges && cartridgesOfWFChanges.length > 0 && (
            <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  padding: 5,
                }}
              >
                <H2 color={Colors.blue}>Change History</H2>
              </div>

              <Row style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                {cartridgesOfWFChanges.map(cartridge => (
                  <Col
                    key={cartridge.id}
                    style={{ display: 'flex', alignItems: 'start', width: '70%' }}
                  >
                    <LineSeparator />
                    <Row
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 5,
                        width: '100%',
                      }}
                    >
                      <H5 color={Colors.blue}>
                        {moment(cartridge.created_at).format('MM/DD/YYYY')}
                      </H5>
                      <H5 style={{ marginLeft: 3 }} color={Colors.blue}>
                        {cartridge.User ? cartridge.User.email : 'Unknown'}
                      </H5>
                    </Row>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </Modal>

        <ModalDatePicker
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerConsumption}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromConsumption')}
          handleDateTo={value => this.handleDate(value, 'datePickerToConsumption')}
          switchModal={() => this.switchModal('modalDatePickerConsumption')}
          dateFrom={datePickerFromConsumption}
          dateTo={datePickerToConsumption}
          interval={consumptionInterval}
          intervalOptions={CONSUMPTION_INTERVAL_GROUPS}
          onIntervalChange={value => this.setState({ consumptionInterval: value })}
          saveChanges={() => {
            this.switchModal('modalDatePickerConsumption');
            this.calculeWaterFilterConsumption();
          }}
        />

        <ModalDatePicker
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerMaxFlowRate}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromMaxFlowRate')}
          handleDateTo={value => this.handleDate(value, 'datePickerToMaxFlowRate')}
          switchModal={() => this.switchModal('modalDatePickerMaxFlowRate')}
          dateFrom={datePickerFromMaxFlowRate}
          dateTo={datePickerToMaxFlowRate}
          interval={maxFlowRateInterval}
          intervalOptions={CONSUMPTION_INTERVAL_GROUPS}
          onIntervalChange={value => this.setState({ maxFlowRateInterval: value })}
          saveChanges={() => {
            this.switchModal('modalDatePickerMaxFlowRate');
            this.calculeWaterFilterMaxFlowRate();
          }}
        />

        <ModalDatePicker
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerBypass}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromBypass')}
          handleDateTo={value => this.handleDate(value, 'datePickerToBypass')}
          switchModal={() => this.switchModal('modalDatePickerBypass')}
          dateFrom={datePickerFromBypass}
          dateTo={datePickerToBypass}
          interval={bypassInterval}
          intervalOptions={CONSUMPTION_INTERVAL_GROUPS}
          onIntervalChange={value => this.setState({ bypassInterval: value })}
          saveChanges={() => {
            this.switchModal('modalDatePickerBypass');
            this.calculeWaterFilterBypass();
          }}
        />

        <ModalDatePicker
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerReport}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromReport')}
          handleDateTo={value => this.handleDate(value, 'datePickerToReport')}
          switchModal={() => this.switchModal('modalDatePickerReport')}
          dateFrom={datePickerFromReport}
          dateTo={datePickerToReport}
          saveChanges={() => {
            this.switchModal('modalDatePickerReport');
            this.generateReport();
          }}
        />

        <Modal
          title="Update Device ID (Serial Number)"
          onClose={() => this.switchModal('modalDeviceId')}
          isVisible={modalDeviceId}
          size="small"
        >
          <Row>
            <Col>
              <Input
                placeholder="Device ID"
                value={new_device_id}
                onChange={this.handleChangeDeviceId}
                style={{ marginBottom: '20px' }}
              />
              <ButtonText
                text="Update"
                theme="primary"
                onClick={() =>
                  setDeviceId({ type: 'water-filter', device_id: new_device_id, location_id })
                }
              />
            </Col>
          </Row>
        </Modal>

        <Modal
          title="Bypass Flow"
          onClose={() => this.switchModal('modalBypass')}
          isVisible={modalBypass}
          size="large"
        >
          {this.renderBypassConsumptions()}
        </Modal>

        {renderInstructionsModal(
          this.state.modalViewInstructions,
          () => this.switchModal('modalViewInstructions'),
          WatherFilterInstallSteps,
        )}
      </Container>
    );
  }
}

function mapStateToProps(state) {
  return {
    waterFilterCurrentState: state.locations.waterFilterCurrentState,
    waterFilterConsumption: state.locations.waterFilterConsumption,
    waterFilterConsumptionLoading: state.locations.waterFilterConsumptionLoading,
    waterFilterMaxFlowRate: state.locations.waterFilterMaxFlowRate,
    waterFilterMaxFlowRateLoading: state.locations.waterFilterMaxFlowRateLoading,
    waterFilterPressureForFlowRate: state.locations.waterFilterPressureForFlowRate,
    waterFilterPressureForFlowRateLoading: state.locations.waterFilterPressureForFlowRateLoading,
    waterFilterPressureForConsumption: state.locations.waterFilterPressureForConsumption,
    waterFilterPressureForConsumptionLoading:
      state.locations.waterFilterPressureForConsumptionLoading,
    totalConsumptionWaterFilterLoading: state.locations.totalConsumptionWaterFilterLoading,
    waterFiltercurrentStateLoading: state.locations.waterFiltercurrentStateLoading,
    waterFilterTotalConsumption: state.locations.waterFilterTotalConsumption,
    waterFilterBypass: state.locations.waterFilterBypass,
    waterFilterBypassLoading: state.locations.waterFilterBypassLoading,
    waterFilterCurrentPressure: state.locations.waterFilterCurrentPressure,
    waterFilterCurrentPressureLoading: state.locations.waterFilterCurrentPressureLoading,
    waterFilterCurrentBypass: state.locations.waterFilterCurrentBypass,
    waterFilterCurrentBypassLoading: state.locations.waterFilterCurrentBypassLoading,
    cartridgeTotalConsumptionWf: state.locations.cartridgeTotalConsumptionWf,
    cartridgeAllTotalConsumptionWf: state.locations.cartridgeAllTotalConsumptionWf,
    cartridgeTotalConsumptionWfLoading: state.locations.cartridgeTotalConsumptionWfLoading,
    cartridgeAllTotalConsumptionWfLoading: state.locations.cartridgeAllTotalConsumptionWfLoading,
    wf_for_pfd: state.locations.wf_for_pfd,
    role_id: state.auth.profile.role_id,
    userEmail: state.auth.profile?.email,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    waterFilterSetInitialState: () => dispatch(waterFilterSetInitialState()),
    getWaterFilterCurrentState: params => dispatch(getWaterFilterCurrentState(params)),
    getWaterFilterCurrentPressure: params => dispatch(getWaterFilterCurrentPressure(params)),
    getWaterFilterConsumption: params => dispatch(getWaterFilterConsumption(params)),
    getWaterFilterMaxFlowRate: params => dispatch(getWaterFilterMaxFlowRate(params)),
    getWaterFilterPressureForConsumption: params =>
      dispatch(getWaterFilterPressureForConsumption(params)),
    getWaterFilterPressureForFlowRate: params =>
      dispatch(getWaterFilterPressureForFlowRate(params)),
    getWaterFilterBypass: params => dispatch(getWaterFilterBypass(params)),
    getTotalConsumptionWaterFilter: params => dispatch(getTotalConsumptionWaterFilter(params)),
    generateWaterFilterCsv: params => dispatch(generateWaterFilterCsv(params)),
    getWaterFilterCurrentBypass: params => dispatch(getWaterFilterCurrentBypass(params)),
    getTotalConsumptionCartridgeWf: params => dispatch(getTotalConsumptionCartridgeWf(params)),
    getAllTotalConsumptionCartridgeWf: params =>
      dispatch(getAllTotalConsumptionCartridgeWf(params)),
    replaceWaterFilterCartridges: params => dispatch(replaceWaterFilterCartridges(params)),
  };
}

/* eslint-disable react/no-unused-prop-types */
WF.propTypes = {
  active: PropTypes.bool,
  allAlerts: PropTypes.array,
  cartridgeAllTotalConsumptionWf: PropTypes.number,
  cartridgeAllTotalConsumptionWfLoading: PropTypes.bool,
  cartridgeTotalConsumptionWf: PropTypes.number,
  cartridgeTotalConsumptionWfLoading: PropTypes.bool,
  device_id: PropTypes.string,
  fetchAllAlerts: PropTypes.func,
  generateWaterFilterCsv: PropTypes.func,
  getAllTotalConsumptionCartridgeWf: PropTypes.func,
  getTotalConsumptionCartridgeWf: PropTypes.func,
  getTotalConsumptionWaterFilter: PropTypes.func,
  getWaterFilterBypass: PropTypes.func,
  getWaterFilterConsumption: PropTypes.func,
  getWaterFilterCurrentBypass: PropTypes.func,
  getWaterFilterCurrentPressure: PropTypes.func,
  getWaterFilterCurrentState: PropTypes.func,
  getWaterFilterMaxFlowRate: PropTypes.func,
  getWaterFilterPressureForConsumption: PropTypes.func,
  getWaterFilterPressureForFlowRate: PropTypes.func,
  location: PropTypes.object,
  payload: PropTypes.array,
  renderInstructionsModal: PropTypes.func,
  replaceWaterFilterCartridges: PropTypes.func,
  role_id: PropTypes.number,
  setDeviceDisconnected: PropTypes.func,
  setDeviceId: PropTypes.func,
  setLastUpdatedLoading: PropTypes.func,
  totalConsumptionWaterFilterLoading: PropTypes.bool,
  updateLastUpdated: PropTypes.func,
  userEmail: PropTypes.string,
  waterFilterBypass: PropTypes.array,
  waterFilterBypassLoading: PropTypes.bool,
  waterFilterBypassSetting: PropTypes.object,
  waterFilterCartridges: PropTypes.array,
  waterFilterCartridgesLoading: PropTypes.bool,
  waterFilterConsumption: PropTypes.array,
  waterFilterConsumptionLoading: PropTypes.bool,
  waterFilterCurrentBypass: PropTypes.array,
  waterFilterCurrentBypassLoading: PropTypes.bool,
  waterFilterCurrentPressure: PropTypes.array,
  waterFilterCurrentPressureLoading: PropTypes.bool,
  waterFilterCurrentState: PropTypes.object,
  waterFiltercurrentStateLoading: PropTypes.bool,
  waterFilterMaxFlowRate: PropTypes.array,
  waterFilterMaxFlowRateLoading: PropTypes.bool,
  waterFilterPressureForConsumption: PropTypes.array,
  waterFilterPressureForConsumptionLoading: PropTypes.bool,
  waterFilterPressureForFlowRate: PropTypes.array,
  waterFilterPressureForFlowRateLoading: PropTypes.bool,
  waterFilterSetInitialState: PropTypes.func,
  waterFilterTotalConsumption: PropTypes.number,
  wf_for_pfd: PropTypes.bool,
};
export default connect(mapStateToProps, mapDispatchToProps)(WF);
